import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Shield, Upload, Trash2, X, FileText, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * VehicleInsuranceCardModal — Fleet Back-Office UI for uploading a per-vehicle
 * insurance card (image or PDF). Once uploaded, every future rental agreement
 * for this vehicle automatically embeds the card into the contract PDF's
 * damage-evidence appendix — no per-booking staff step required.
 *
 * Managers can also stash carrier / policy # / expiration metadata alongside
 * the file so the audit trail is complete even before the image is opened.
 */
export default function VehicleInsuranceCardModal({ vehicle, onClose, onSaved }) {
  const [carrier, setCarrier] = useState(vehicle.insurance_carrier || "");
  const [policy, setPolicy] = useState(vehicle.insurance_policy_number || "");
  const [expiration, setExpiration] = useState(vehicle.insurance_expiration || "");
  const [fileId, setFileId] = useState(vehicle.insurance_card_file_id || null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const cardUrl = fileId ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/vehicles/${vehicle.id}/insurance-card?v=${fileId}` : null;

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/vehicles/${vehicle.id}/insurance-card`, fd);
      setFileId(data.file_id);
      toast.success("Insurance card uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeCard = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/vehicles/${vehicle.id}/insurance-card`);
      setFileId(null);
      toast.success("Insurance card removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/vehicles/${vehicle.id}`, {
        insurance_carrier: carrier.trim() || null,
        insurance_policy_number: policy.trim() || null,
        insurance_expiration: expiration || null,
      });
      toast.success("Saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const isPdf = false; // we can't tell from the URL alone; try image first
  return (
    <div
      data-testid="vehicle-insurance-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-center justify-center p-4 overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-ink-900 border border-electric/40 rounded-3xl p-6 my-8 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps text-electric text-xs inline-flex items-center gap-1.5">
              <Shield size={12} /> Vehicle insurance card
            </div>
            <div className="font-serif text-lg mt-0.5">{vehicle.name}</div>
            <div className="text-[11px] text-white/50 mt-0.5">
              Automatically embedded in every rental agreement PDF for this vehicle.
            </div>
          </div>
          <button
            type="button"
            data-testid="vehicle-insurance-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Card preview / uploader */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
          {fileId && cardUrl ? (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden bg-black border border-white/10">
                {isPdf ? (
                  <div className="p-8 text-center text-white/60 flex flex-col items-center gap-2">
                    <FileText size={28} />
                    <a href={cardUrl} target="_blank" rel="noopener noreferrer"
                       className="text-electric underline text-sm">Open insurance card PDF</a>
                  </div>
                ) : (
                  <img
                    src={cardUrl}
                    alt="Insurance card"
                    data-testid="vehicle-insurance-preview"
                    className="w-full max-h-96 object-contain bg-black"
                    onError={(e) => {
                      // Fall back to PDF link when the file isn't an image
                      const link = document.createElement("div");
                      link.className = "p-8 text-center text-white/60 text-sm";
                      link.innerHTML = `<a href="${cardUrl}" target="_blank" class="text-electric underline">Open insurance card</a>`;
                      e.currentTarget.replaceWith(link);
                    }}
                  />
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] label-caps border border-electric/40 text-electric hover:bg-electric/10 cursor-pointer">
                  <Upload size={12} /> Replace
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,application/pdf"
                    data-testid="vehicle-insurance-input"
                    onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden"
                  />
                </label>
                <button
                  type="button"
                  data-testid="vehicle-insurance-delete"
                  onClick={removeCard}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] label-caps border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-40"
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            </div>
          ) : (
            <label className="w-full aspect-[3/2] rounded-xl border-2 border-dashed border-electric/40 flex flex-col items-center justify-center text-electric hover:bg-electric/5 cursor-pointer">
              {uploading ? (
                <><Loader2 className="animate-spin" size={22} />
                  <span className="text-[11px] label-caps mt-2">Uploading…</span></>
              ) : (
                <><Upload size={22} />
                  <span className="text-[11px] label-caps mt-2">Upload insurance card (image or PDF)</span>
                  <span className="text-[10px] text-white/40 mt-0.5">Auto-attached to every future rental agreement</span></>
              )}
              <input
                type="file"
                accept="image/*,application/pdf"
                data-testid="vehicle-insurance-input"
                onChange={(e) => upload(e.target.files?.[0])}
                disabled={uploading}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="label-caps text-[10px] text-white/50 block mb-1">Carrier</span>
            <input
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="e.g. Progressive Commercial"
              data-testid="vehicle-insurance-carrier"
              className="w-full bg-ink-950/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30"
            />
          </label>
          <label className="block">
            <span className="label-caps text-[10px] text-white/50 block mb-1">Policy #</span>
            <input
              value={policy}
              onChange={(e) => setPolicy(e.target.value)}
              placeholder="e.g. 12345-6789"
              data-testid="vehicle-insurance-policy"
              className="w-full bg-ink-950/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30"
            />
          </label>
          <label className="block">
            <span className="label-caps text-[10px] text-white/50 block mb-1">Expiration</span>
            <input
              type="date"
              value={expiration}
              onChange={(e) => setExpiration(e.target.value)}
              data-testid="vehicle-insurance-expiration"
              min={new Date().toISOString().slice(0,10)}
              className="w-full bg-ink-950/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
          <button
            type="button"
            data-testid="vehicle-insurance-cancel"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-xs label-caps border border-white/15 text-white/70 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="vehicle-insurance-save"
            onClick={saveMeta}
            disabled={busy}
            className="px-4 py-2 rounded-full text-xs label-caps bg-electric text-midnight font-semibold hover:brightness-110 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
